import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import Marque from "../components/Marque";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1633759593085-1eaeb724fc88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxyb29mZXIlMjBpbnN0YWxsaW5nJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzg1MTI1NTc4fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t, lang } = useI18n();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleClientId, setGoogleClientId] = useState("");
  const [oubliEnvoi, setOubliEnvoi] = useState(false);

  // La connexion Google n'est proposee que si le serveur est configure pour.
  useEffect(() => {
    api.get("/auth/google/config")
      .then(({ data }) => { if (data?.enabled) setGoogleClientId(data.client_id); })
      .catch(() => setGoogleClientId(""));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      toast.success(mode === "login" ? t("auth.welcomeBack") : t("auth.accountCreated"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Réinitialisation : la réponse est volontairement la même que le compte
  // existe ou non, sinon la page dirait publiquement qui est client.
  const oublie = async () => {
    const email = (form.email || "").trim();
    if (!email) {
      toast.error(t("auth.enterEmailFirst"));
      return;
    }
    setOubliEnvoi(true);
    try {
      await api.post("/compte/oubli", { email, lang });
      toast.success(t("auth.resetSent"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.failed"));
    } finally {
      setOubliEnvoi(false);
    }
  };

  const googleLogin = () => {
    const redirectUri = window.location.origin + "/auth/callback";
    // Jeton anti-CSRF à usage unique : rangé ici, revérifié au retour dans
    // AuthCallback. Empêche qu'un flux OAuth d'attaquant soit rejoué sur la victime.
    const state = (crypto.randomUUID?.() || String(Math.random())).replace(/-/g, "");
    sessionStorage.setItem("oauth_state", state);
    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  return (
    <div className="min-h-screen bg-app text-ink grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={AUTH_BG} alt="Roofer" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        {/* Panneau posé sur une photo assombrie : le texte est blanc en dur,
            peu importe le thème — les jetons d'encre deviendraient noirs en
            mode clair et disparaîtraient dans la photo. */}
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Marque taille="xl" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-4">{t("auth.heroOverline")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">
              {t("auth.heroTitle1")}<br />{t("auth.heroTitle2")}
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">{t("auth.heroDesc")}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-8">
            <Marque taille="lg" variante="etroit" className="lg:hidden" />
            <div className="ml-auto flex items-center gap-2"><ThemeSwitch /><LangSwitch langues={["en", "fr", "es"]} /></div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
            {mode === "login" ? t("auth.signIn") : t("auth.createAccount")}
          </h2>
          <p className="text-sm text-ink-faint mb-6">
            {mode === "login" ? t("auth.signInSub") : t("auth.createAccountSub")}
          </p>

          {googleClientId && (<>
          <button onClick={googleLogin} data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-medium py-2.5 rounded-md hover:bg-neutral-200 transition-colors mb-4">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            {t("auth.continueGoogle")}
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-hover-strong" />
            <span className="text-xs text-ink-faint uppercase tracking-widest">{t("auth.or")}</span>
            <div className="h-px flex-1 bg-hover-strong" />
          </div>
          </>)}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("auth.fullName")}</Label>
                  <Input data-testid="register-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("auth.company")}</Label>
                  <Input data-testid="register-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("auth.email")}</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("auth.password")}</Label>
              <Input data-testid="password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
              {mode === "login" && (
                <button type="button" data-testid="mot-de-passe-oublie" onClick={oublie} disabled={oubliEnvoi}
                        className="text-xs text-ink-faint hover:text-brand-ink transition-colors mt-2">
                  {t("auth.forgotPassword")}
                </button>
              )}
            </div>
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full bg-orange-600 hover:bg-orange-700 rounded-md font-semibold h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? t("auth.signInBtn") : t("auth.createAccountBtn")}
            </Button>
          </form>

          <p className="text-sm text-ink-faint mt-6 text-center">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-brand-ink hover:text-brand-ink font-medium">
              {mode === "login" ? t("auth.createOne") : t("auth.signInLink")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
